import type { AxiosRequestConfig } from 'axios';
import i18n from '../i18n/config';

/** Background GETs (session probe, counters) should not spam ERR_NETWORK toasts. */
export function shouldSuppressNetworkErrorToast(config: AxiosRequestConfig | undefined): boolean {
  if (!config) return false;
  if (config.skipNetworkErrorToast) return true;
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get') return false;
  let path = String(config.url || '');
  if (path.includes('://')) {
    try {
      path = new URL(path).pathname;
    } catch {
      /* ignore */
    }
  }
  const norm = path.split('?')[0].replace(/\/+/g, '/');
  if (/\/auth\/me$/i.test(norm) || norm.endsWith('auth/me')) return true;
  if (/\/messages\/unread-count$/i.test(norm) || /messages\/unread-count$/i.test(norm)) return true;
  return false;
}

/** True when the browser got no HTTP response (offline, refused, CORS, timeout). */
export function isAxiosNetworkError(err: unknown): boolean {
  const ax = err as { response?: unknown; message?: string; code?: string };
  if (ax?.response != null) return false;
  const code = String(ax?.code || '');
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;
  const message = String(ax?.message || '');
  return message === 'Network Error' || message.toLowerCase().includes('network');
}

/** Unwrap `{ success: true, data }` or return legacy body. */
export function unwrapApiData<T = Record<string, unknown>>(body: unknown): T {
  const b = body as { success?: boolean; data?: unknown } | null | undefined;
  if (b && b.success === true && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

/** Localized message from axios error or API JSON body. */
export function getApiErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const d = ax?.response?.data;
  if (!d) {
    const net = ax?.message === 'Network Error' || String(ax?.message || '').toLowerCase().includes('network');
    if (net) return i18n.t('errors.ERR_NETWORK');
    return i18n.t('errors.ERR_UNKNOWN');
  }

  const errObj = d.error;
  if (errObj && typeof errObj === 'object' && errObj !== null && 'code' in errObj) {
    const code = String((errObj as { code?: string }).code || '');
    if (code) {
      const key = `errors.${code}`;
      const msg = i18n.t(key);
      if (msg !== key) return msg;
      const m = (errObj as { message?: string }).message;
      if (m) return m;
    }
  }

  if (typeof errObj === 'string') return errObj;

  const top = d.message;
  if (typeof top === 'string' && top.length > 0) return top;

  return i18n.t('errors.ERR_UNKNOWN');
}
